import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from './client';
import type { AppointmentType, ChargeItem, Department, LabTest, Locale, Medication, Practitioner } from './types';
import { useToast } from '@/components/ui';
import { errorMessage } from '@/utils/errors';

const STATIC = { staleTime: 5 * 60_000 };

export const useDepartments = () => useQuery({ queryKey: ['departments'], queryFn: () => api.get<Department[]>('/api/v1/departments'), ...STATIC });
export const usePractitioners = (departmentId?: string) =>
  useQuery({
    queryKey: ['practitioners', departmentId ?? ''],
    queryFn: () => api.get<Practitioner[]>('/api/v1/practitioners', { departmentId }),
    ...STATIC,
  });
export const useAppointmentTypes = () =>
  useQuery({ queryKey: ['appointment-types'], queryFn: () => api.get<AppointmentType[]>('/api/v1/appointment-types'), ...STATIC });
export const useMedications = (q = '', all = false) =>
  useQuery({ queryKey: ['medications', q, all], queryFn: () => api.get<Medication[]>('/api/v1/medications', { q, all }), ...STATIC });
export const useLabTests = () => useQuery({ queryKey: ['lab-tests'], queryFn: () => api.get<LabTest[]>('/api/v1/lab/tests'), ...STATIC });
export const useChargeItems = () => useQuery({ queryKey: ['charge-items'], queryFn: () => api.get<ChargeItem[]>('/api/v1/charge-items'), ...STATIC });
export const useLocales = () =>
  useQuery({ queryKey: ['locales'], queryFn: () => api.publicGet<Locale[]>('/api/v1/i18n/locales'), ...STATIC });

/**
 * Mutation wrapper: toasts a translated error (unless it is a 422 handled by
 * the form), shows an optional success message and invalidates query keys.
 */
export function useApiMutation<TVars, TResult>(
  fn: (vars: TVars) => Promise<TResult>,
  opts: {
    invalidate?: QueryKey[];
    success?: string | ((r: TResult) => string);
    onSuccess?: (r: TResult, vars: TVars) => void;
    silentCodes?: string[];
  } = {},
) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: fn,
    onSuccess: (result, vars) => {
      for (const key of opts.invalidate ?? []) void qc.invalidateQueries({ queryKey: key });
      if (opts.success) toast(typeof opts.success === 'function' ? opts.success(result) : t(opts.success));
      opts.onSuccess?.(result, vars);
    },
    onError: (err: unknown) => {
      const code = (err as { code?: string }).code;
      if (code && opts.silentCodes?.includes(code)) return;
      toast(errorMessage(err), 'error');
    },
  });
}
